import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPinIcon, CupSodaIcon, ClockIcon, ArrowRightIcon } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { Button } from '../components/ui/Button';
import { SmartImage } from '../components/common/SmartImage';
import { StatusBadge } from '../components/common/StatusBadge';
import { UserGallery } from '../components/home/UserGallery';
import { HowItWorks } from '../components/home/HowItWorks';
import { useI18n } from '../lib/i18n';
import { IMAGES } from '../lib/images';
import { MACHINE, DRINKS } from '../lib/data';
export function Landing() {
  const {
    t,
    lang
  } = useI18n();
  const navigate = useNavigate();
  const availableCount = DRINKS.filter(d => d.inStock).length;
  return <PageShell data-id="element-311" data-mp="h9tnc6nmmr3p">
      {/* Hero */}
      <section className="relative overflow-hidden" data-id="element-312" data-mp="h6e8n4xv7k2t">
        <div className="mx-auto grid max-w-container items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20" data-id="element-313" data-mp="m8n365qdyu71">
          <div data-id="element-314" data-mp="2blha459uj8t">
            <SmartImage src={IMAGES.productLogo} alt="RevoV — Revolutionized Your Life" className="h-16 w-auto object-contain" data-id="element-315" data-mp="27xleycctkqx" />
            <h1 className="mt-6 text-4xl font-extrabold leading-tight text-white sm:text-5xl" data-id="element-316" data-mp="3pf9pbw1vjt1d">
              {t('hero_headline')}
            </h1>
            <p className="mt-4 text-lg text-silver/80" data-id="element-317" data-mp="l09yog4jq6fu">{t('hero_sub')}</p>
            <p className="mt-1 text-base text-silver/60" data-id="element-318" data-mp="l2maebjpnkkg">{t('hero_desc')}</p>

            <div className="mt-8" data-id="element-319" data-mp="gzg97ibggjku">
              <Button pulse onClick={() => navigate('/verify/' + MACHINE.id)} data-id="element-320" data-mp="c880kohy928q">
                {t('start_order')}
                <ArrowRightIcon className="h-4 w-4" data-id="element-321" data-mp="79d9z0dqq9e2" />
              </Button>
            </div>

            {/* Machine status card */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.4
          }} className="glass-card mt-8 grid grid-cols-1 gap-4 p-5 sm:grid-cols-2" data-id="element-322" data-mp="14pak3uc7ry2g">
              <div data-id="element-323" data-mp="1a17l7nf9sdzv">
                <p className="text-xs uppercase tracking-wide text-silver/50" data-id="element-324" data-mp="19zmphuh7yp0f">
                  {t('machine')}
                </p>
                <p className="mt-1 text-base font-semibold text-white" data-id="element-325" data-mp="10309bizu8m4q">
                  {MACHINE.id}
                </p>
                <div className="mt-2" data-id="element-326" data-mp="153nv9q97mcs3">
                  <StatusBadge status={MACHINE.status} data-id="element-327" data-mp="150jm0egw0quk" />
                </div>
              </div>
              <div className="space-y-2 text-sm text-silver/80" data-id="element-328" data-mp="r4y2gmtfaz9j">
                <p className="flex items-center gap-2" data-id="element-329" data-mp="v584joe3h5j4">
                  <MapPinIcon className="h-4 w-4 text-neon" data-id="element-330" data-mp="2lh5pldzi6bur" />
                  {lang === 'en' ? MACHINE.location_en : MACHINE.location_am}
                </p>
                <p className="flex items-center gap-2" data-id="element-331" data-mp="2phokjgwozy16">
                  <CupSodaIcon className="h-4 w-4 text-neon" data-id="element-332" data-mp="dumcnu5ojt0p" />
                  {availableCount} {t('products_available')}
                </p>
                <p className="flex items-center gap-2" data-id="element-333" data-mp="9vdnl2ll4jbh">
                  <ClockIcon className="h-4 w-4 text-neon" data-id="element-334" data-mp="nl7b9hlcsxjm" />
                  {t('last_online')}: 2 {t('min_ago')}
                </p>
              </div>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="relative" data-id="element-335" data-mp="xilprbnpvc9a">
            <div className="glass-card overflow-hidden p-3" data-id="element-336" data-mp="11lrugev6kvdn">
              <SmartImage src={IMAGES.productImage} alt="RevoV autonomous vending machine" className="h-[420px] w-full rounded-lg object-cover sm:h-[520px]" data-id="element-337" data-mp="38b6itpyhl4lp" />
            </div>
          </motion.div>
        </div>
      </section>

      <HowItWorks data-id="element-338" data-mp="35t2ynkkfmb1f" />

      {/* Users gallery */}
      <section className="py-12" aria-labelledby="users-title" data-id="element-339" data-mp="39rakgttr62jh">
        <div className="mx-auto max-w-container px-4 text-center sm:px-6" data-id="element-340" data-mp="3erogfdi2xm15">
          <h2 id="users-title" className="text-2xl font-bold text-white sm:text-3xl" data-id="element-341" data-mp="3epc3xfv3c3vh">
            {t('users_title')}
          </h2>
          <p className="mt-2 text-silver/70" data-id="element-342" data-mp="2m42ahyldq5b4">{t('users_sub')}</p>
        </div>
        <div className="mt-8" data-id="element-343" data-mp="2r12m0p10009m">
          <UserGallery data-id="element-344" data-mp="2r2dpsoug180q" />
        </div>
      </section>
    </PageShell>;
}